import type { SupabaseClient } from '@supabase/supabase-js';

type Severity = 'info' | 'warning' | 'error' | 'critical';

interface MonitoringOptions {
  appName: 'AbstiCare' | 'BehaCare' | 'RS Time';
  supabase: SupabaseClient | null;
  environment?: string;
}

const EVENT_THROTTLE_MS = 60 * 1000;
const MAX_TEXT_LENGTH = 120_000;

let isInstalled = false;
let isReporting = false;
let originalConsoleError: typeof console.error | null = null;
let originalFetch: typeof window.fetch | null = null;
const recentEvents = new Map<string, number>();

const getSessionId = () => {
  const existing = window.sessionStorage.getItem('app-monitoring-session-id');
  if (existing) return existing;

  const created = crypto.randomUUID();
  window.sessionStorage.setItem('app-monitoring-session-id', created);
  return created;
};

const summarizeValue = (value: unknown, depth = 0): unknown => {
  if (depth > 2) return '[gekürzt]';
  if (value instanceof Error) {
    return {
      name: value.name,
      message: value.message,
      stack: value.stack?.slice(0, MAX_TEXT_LENGTH),
    };
  }
  if (value === null || value === undefined) return value;
  if (typeof value === 'string') return value.slice(0, MAX_TEXT_LENGTH);
  if (typeof value === 'number' || typeof value === 'boolean') return value;
  if (Array.isArray(value)) return value.slice(0, 8).map((entry) => summarizeValue(entry, depth + 1));
  if (typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value as Record<string, unknown>)
        .filter(([key]) => !/password|passwort|token|authorization|access_token|refresh_token/i.test(key))
        .slice(0, 30)
        .map(([key, entry]) => [key, summarizeValue(entry, depth + 1)]),
    );
  }
  return String(value).slice(0, MAX_TEXT_LENGTH);
};

const getErrorMessage = (error: unknown): string => {
  if (error instanceof Error) return error.message;
  if (typeof error === 'string') return error;
  if (error && typeof error === 'object' && 'message' in error) return String(error.message);
  return 'Unbekannter Fehler';
};

const getErrorStack = (error: unknown): string | null => {
  if (error instanceof Error) return error.stack ?? null;
  if (error && typeof error === 'object' && 'stack' in error) return String(error.stack).slice(0, MAX_TEXT_LENGTH);
  return null;
};

const classifyKnownError = (message: string) => {
  const normalized = message.toLowerCase();

  if (
    normalized.includes('failed to fetch dynamically imported module')
    || normalized.includes('chunkloaderror')
    || normalized.includes('loading css chunk')
  ) {
    return {
      severity: 'warning' as Severity,
      area: 'asset-rollout',
    };
  }

  return null;
};

const shouldSend = (message: string, route: string): boolean => {
  const signature = `${route}:${message}`.slice(0, 500);
  const now = Date.now();
  const lastSent = recentEvents.get(signature) ?? 0;
  if (now - lastSent < EVENT_THROTTLE_MS) return false;

  recentEvents.set(signature, now);
  return true;
};

export function installAppErrorMonitoring({ appName, supabase, environment }: MonitoringOptions): void {
  if (isInstalled || typeof window === 'undefined') return;

  isInstalled = true;
  const configuredEnvironment = environment ?? import.meta.env.MODE ?? import.meta.env.VITE_APP_ENV ?? 'production';

  const report = async (
    error: unknown,
    context: { area?: string; severity?: Severity; metadata?: Record<string, unknown> } = {},
  ) => {
    if (isReporting) return;

    const message = getErrorMessage(error).slice(0, 2000);
    const knownClassification = classifyKnownError(message);
    const route = `${window.location.pathname}${window.location.search}${window.location.hash}`;
    if (!shouldSend(message, route)) return;

    isReporting = true;
    try {
      const session = supabase ? (await supabase.auth.getSession()).data.session : null;
      const body = {
        app_name: appName,
        source: 'frontend',
        severity: knownClassification?.severity ?? context.severity ?? 'error',
        environment: configuredEnvironment,
        area: knownClassification?.area ?? context.area ?? 'browser',
        message,
        stack_trace: getErrorStack(error),
        error_name: error instanceof Error ? error.name : null,
        route,
        user_id: session?.user?.id ?? null,
        session_id: getSessionId(),
        user_agent: navigator.userAgent,
        metadata: {
          url: window.location.href,
          language: navigator.language,
          online: navigator.onLine,
          viewport: { width: window.innerWidth, height: window.innerHeight },
          ...context.metadata,
        },
      };

      const supabaseUrl = String(
        import.meta.env.VITE_ERROR_MONITORING_URL ?? import.meta.env.VITE_SUPABASE_URL ?? '',
      ).replace(/\/+$/, '');
      const anonKey = String(import.meta.env.VITE_SUPABASE_ANON_KEY ?? '');
      if (!supabaseUrl || !anonKey) return;

      await fetch(`${supabaseUrl}/functions/v1/log-app-event`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          apikey: anonKey,
          ...(session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {}),
        },
        body: JSON.stringify(body),
        keepalive: true,
      }).catch(() => undefined);
    } finally {
      isReporting = false;
    }
  };

  window.addEventListener('error', (event) => {
    void report(event.error ?? event.message, {
      area: 'window.onerror',
      metadata: {
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
      },
    });
  });

  window.addEventListener('unhandledrejection', (event) => {
    void report(event.reason, { area: 'window.onunhandledrejection' });
  });

  originalConsoleError = console.error.bind(console);
  console.error = (...args: unknown[]) => {
    originalConsoleError?.(...args);
    if (!isReporting) {
      void report(args[0] instanceof Error ? args[0] : new Error(args.map((arg) => getErrorMessage(arg)).join(' ')), {
        area: 'console.error',
        severity: 'warning',
        metadata: { arguments: summarizeValue(args) },
      });
    }
  };

  originalFetch = window.fetch.bind(window);
  window.fetch = async (...args: Parameters<typeof window.fetch>) => {
    const response = await originalFetch!(...args);
    const requestUrl = String(args[0] ?? '');
    if (!requestUrl.includes('/functions/v1/log-app-event') && response.status >= 500) {
      const responseText = await response.clone().text().catch(() => '');
      void report(new Error(responseText || `HTTP ${response.status} ${response.statusText}`), {
        area: 'fetch',
        severity: response.status >= 500 ? 'error' : 'warning',
        metadata: {
          status: response.status,
          statusText: response.statusText,
          url: requestUrl,
          response: responseText.slice(0, 2000),
        },
      });
    }
    return response;
  };
}
